import { getDeviceTimezone } from '@/utils/date';
import { transformToManagerForecastTiles } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/transformers/manager-prospect-forecast-transformers';
import { useGeneratedSalesPortalManagerProspectForecastQuery } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-manager-prospect-forecast.generated';
import { useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import type { ChatProspectStage } from '@/types';
import type { ManagerForecastTileData } from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';

interface UseManagerProspectForecastLoaderParams {
  businessOrganizationUUID: string;
  employeeUUIDs: string[];
  stages: ChatProspectStage[];
}

interface UseManagerProspectForecastLoaderResult {
  forecastError?: ApolloError;
  forecastLoading: boolean;
  forecastTiles: ManagerForecastTileData[];
  refetchForecast: () => void;
}

export function useManagerProspectForecastLoader({
  businessOrganizationUUID,
  employeeUUIDs,
  stages,
}: UseManagerProspectForecastLoaderParams): UseManagerProspectForecastLoaderResult {
  const userTimezone = getDeviceTimezone();

  const {
    data: forecastData,
    error: forecastError,
    loading: forecastLoading,
    refetch,
  } = useGeneratedSalesPortalManagerProspectForecastQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      businessOrganizationUUID,
      employeeIds: employeeUUIDs,
      stages,
      userTimezone,
    },
  });

  const forecastTiles = useMemo(
    () =>
      transformToManagerForecastTiles(
        forecastData?.getSalesPortalManagerProspectForecast ?? [],
      ),
    [forecastData],
  );

  return {
    forecastError,
    forecastLoading,
    forecastTiles,
    refetchForecast: () => {
      refetch().catch(() => {});
    },
  };
}
